/**
 * Página de Listagem de Respostas de uma Pesquisa
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { API_CONFIG } from '../../../services/staticDataConfig';

interface DetalheResposta {
  pergunta_id: number;
  pergunta_titulo: string;
  tipo: string;
  resposta: string | number | string[];
}

interface RespostaCompleta {
  id: number;
  pesquisa_id: number;
  usuario_id?: number;
  usuario_nome?: string;
  ip_address?: string;
  tempo_resposta_segundos?: number;
  created_at: string;
  detalhes: DetalheResposta[];
}

interface Paginacao {
  pagina: number;
  limite: number;
  total: number;
  totalPaginas: number;
}

export function RespostasPesquisa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAdminAuth();
  
  const [loading, setLoading] = useState(true);
  const [respostas, setRespostas] = useState<RespostaCompleta[]>([]);
  const [paginacao, setPaginacao] = useState<Paginacao>({ pagina: 1, limite: 20, total: 0, totalPaginas: 0 });
  const [respostaSelecionada, setRespostaSelecionada] = useState<RespostaCompleta | null>(null);

  const carregarRespostas = useCallback(async (pagina = 1) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/pesquisas/admin/${id}/respostas?pagina=${pagina}&limite=20`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setRespostas(data.dados || []);
        setPaginacao(data.paginacao || { pagina: 1, limite: 20, total: 0, totalPaginas: 0 });
      }
    } catch (error) {
      console.error('Erro ao carregar respostas:', error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    carregarRespostas();
  }, [carregarRespostas]);

  const excluirResposta = async (respostaId: number) => {
    if (!confirm('Tem certeza que deseja excluir esta resposta?')) return;

    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/pesquisas/admin/respostas/${respostaId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        carregarRespostas(paginacao.pagina);
        setRespostaSelecionada(null);
      }
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert('Erro ao excluir resposta');
    }
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleString('pt-BR');
  };

  const formatarTempo = (segundos?: number) => {
    if (!segundos) return '-';
    if (segundos < 60) return `${segundos}s`;
    const minutos = Math.floor(segundos / 60);
    const seg = segundos % 60;
    return `${minutos}m ${seg}s`;
  };

  const formatarResposta = (resposta: string | number | string[]): string => {
    if (Array.isArray(resposta)) {
      return resposta.join(', ');
    }
    return String(resposta);
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="mb-0">
          <i className="fas fa-list-alt me-2"></i>
          Respostas
        </h2>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/admin/pesquisas')}
        >
          <i className="fas fa-arrow-left me-2"></i>
          Voltar
        </button>
      </div>

          <div className="row">
            {/* Lista de Respostas */}
            <div className={respostaSelecionada ? 'col-md-6' : 'col-12'}>
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    {paginacao.total} respostas
                  </h3>
                </div>
                <div className="card-body p-0">
                  {loading ? (
                    <div className="text-center p-5">
                      <div className="spinner-border text-primary"></div>
                    </div>
                  ) : respostas.length === 0 ? (
                    <div className="text-center p-5 text-muted">
                      <i className="fas fa-inbox fa-3x mb-3"></i>
                      <p>Nenhuma resposta recebida</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Data/Hora</th>
                            <th>Respondente</th>
                            <th>Tempo</th>
                            <th className="text-center">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {respostas.map((resposta, index) => (
                            <tr 
                              key={resposta.id}
                              className={respostaSelecionada?.id === resposta.id ? 'table-active' : ''}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setRespostaSelecionada(resposta)}
                            >
                              <td>{(paginacao.pagina - 1) * paginacao.limite + index + 1}</td>
                              <td>{formatarData(resposta.created_at)}</td>
                              <td>
                                {resposta.usuario_nome || (
                                  <span className="text-muted">
                                    Anônimo
                                    {resposta.ip_address && (
                                      <small className="d-block">{resposta.ip_address}</small>
                                    )}
                                  </span>
                                )}
                              </td>
                              <td>{formatarTempo(resposta.tempo_resposta_segundos)}</td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    excluirResposta(resposta.id);
                                  }}
                                  title="Excluir"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Paginação */}
                {paginacao.totalPaginas > 1 && (
                  <div className="card-footer">
                    <nav>
                      <ul className="pagination pagination-sm justify-content-center mb-0">
                        <li className={`page-item ${paginacao.pagina === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link"
                            onClick={() => carregarRespostas(paginacao.pagina - 1)}
                          >
                            Anterior
                          </button>
                        </li>
                        {Array.from({ length: Math.min(5, paginacao.totalPaginas) }, (_, i) => {
                          const inicio = Math.max(1, paginacao.pagina - 2);
                          const pagina = inicio + i;
                          if (pagina > paginacao.totalPaginas) return null;
                          return (
                            <li key={pagina} className={`page-item ${pagina === paginacao.pagina ? 'active' : ''}`}>
                              <button 
                                className="page-link"
                                onClick={() => carregarRespostas(pagina)}
                              >
                                {pagina}
                              </button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${paginacao.pagina === paginacao.totalPaginas ? 'disabled' : ''}`}>
                          <button 
                            className="page-link"
                            onClick={() => carregarRespostas(paginacao.pagina + 1)}
                          >
                            Próximo
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>

            {/* Detalhes da Resposta Selecionada */}
            {respostaSelecionada && (
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h3 className="card-title mb-0">
                      Detalhes da Resposta #{respostaSelecionada.id}
                    </h3>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setRespostaSelecionada(null)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <small className="text-muted">
                        Enviado em: {formatarData(respostaSelecionada.created_at)}
                        {respostaSelecionada.tempo_resposta_segundos && (
                          <> | Tempo: {formatarTempo(respostaSelecionada.tempo_resposta_segundos)}</>
                        )}
                      </small>
                    </div>

                    {respostaSelecionada.detalhes.map((detalhe, index) => (
                      <div key={index} className="mb-3 pb-3 border-bottom">
                        <label className="form-label text-muted small mb-1">
                          {detalhe.pergunta_titulo}
                        </label>
                        <div className="fw-bold">
                          {formatarResposta(detalhe.resposta) || (
                            <span className="text-muted fst-italic">Não respondido</span>
                          )}
                        </div>
                      </div>
                    ))}

                    <div className="d-flex justify-content-end">
                      <button 
                        className="btn btn-danger"
                        onClick={() => excluirResposta(respostaSelecionada.id)}
                      >
                        <i className="fas fa-trash me-2"></i>
                        Excluir Resposta
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

    </div>
  );
}

export default RespostasPesquisa;
